import { FC, useState, useCallback, useMemo } from 'react';
import Picker, { PickerValue } from 'react-mobile-picker';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import { SystemModalContainer } from '@/components/common/SystemModal/SystemModal.tsx';
import { MainButton } from '@/components/common/MainButton/MainButton.tsx';

const MONTH_BY_NAME = (t: TFunction) => [
  t('january'),
  t('february'),
  t('march'),
  t('april'),
  t('may'),
  t('june'),
  t('july'),
  t('august'),
  t('september'),
  t('october'),
  t('november'),
  t('december')
];

interface WheelBirthdaySelectModalProps {
  birthday?: string;
  updateFields: (fields: Partial<{ birthday: string }>) => void;
  handleClose: () => void;
}

const daysByYearMonth = (year: number, monthIndex: number) => {
  const dayCount = new Date(year, monthIndex + 1, 0).getDate();
  return Array.from({ length: dayCount }, (_, i) => String(i + 1));
};

export const WheelBirthdaySelectModal: FC<WheelBirthdaySelectModalProps> = ({
  birthday,
  updateFields,
  handleClose
}) => {
  const { t } = useTranslation();

  const firstYear = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return currentYear - 80;
  }, []);

  const lastYear = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return currentYear - 17;
  }, []);

  const [pickerValue, setPickerValue] = useState<PickerValue>({
    year: birthday ? new Date(birthday).getFullYear() : lastYear,
    month: birthday ? new Date(birthday).getMonth() : '',
    day: birthday ? new Date(birthday).getDate().toString() : ''
  });

  const [birthdayFormatted, setBirthdayFormatted] = useState<
    string | undefined
  >(birthday);

  const handlePickerChange = useCallback(
    (newValue: PickerValue, key: string) => {
      const updatedValue: PickerValue = {
        ...newValue,
        month: newValue.month || '0', // if month was not selected pick Jan by default
        day: newValue.day || '1' // if day was not selected pick first by default
      };

      if (key === 'year' || key === 'month') {
        const newDayArray = daysByYearMonth(
          Number(updatedValue.year),
          Number(updatedValue.month)
        );
        updatedValue.day = newDayArray.includes(updatedValue.day as string)
          ? updatedValue.day
          : newDayArray[0];
      }

      setPickerValue(updatedValue);

      const formattedDate = `${updatedValue.year}-${String(Number(updatedValue.month) + 1).padStart(2, '0')}-${String(updatedValue.day).padStart(2, '0')}`;

      setBirthdayFormatted(formattedDate);
    },
    []
  );

  const handleConfirm = () => {
    updateFields({ birthday: birthdayFormatted || '' });
    handleClose();
  };

  return (
    <SystemModalContainer
      headerTitle={t('select_birthdate_title')}
      handleClose={handleClose}
    >
      <Picker
        value={pickerValue}
        onChange={handlePickerChange}
        wheelMode="natural"
      >
        <Picker.Column name="year">
          {Array.from(
            { length: lastYear - firstYear + 1 },
            (_, i) => lastYear - i
          ).map((year) => (
            <Picker.Item key={year} value={year}>
              {({ selected }) => (
                <p
                  style={{
                    fontWeight: selected ? 600 : 300
                  }}
                >
                  {year}
                </p>
              )}
            </Picker.Item>
          ))}
        </Picker.Column>
        <Picker.Column name="month">
          {MONTH_BY_NAME(t).map((month, index) => (
            <Picker.Item key={index} value={index}>
              {({ selected }) => (
                <p
                  style={{
                    fontWeight: selected ? 600 : 300
                  }}
                >
                  {month}
                </p>
              )}
            </Picker.Item>
          ))}
        </Picker.Column>
        <Picker.Column name="day">
          {daysByYearMonth(
            Number(pickerValue.year),
            Number(pickerValue.month)
          ).map((day) => (
            <Picker.Item key={day} value={day}>
              {({ selected }) => (
                <p
                  style={{
                    fontWeight: selected ? 600 : 300
                  }}
                >
                  {day}
                </p>
              )}
            </Picker.Item>
          ))}
        </Picker.Column>
      </Picker>
      <div style={{ marginTop: '60px' }}>
        <MainButton fixed text={t('button.confirm')} onClick={handleConfirm} />
      </div>
    </SystemModalContainer>
  );
};
